'use client';

import {
  ChevronDown,
  Percent,
  Search,
  ToggleLeft,
  X,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';

import type { BulkEditState } from '../hooks/use-bulk-edit-state';
import { BulkEditColumnPicker } from './bulk-edit-column-picker';

interface BulkEditActionBarProps {
  state: BulkEditState;
  /** Fixed = full-page floating pill; inline = docked under the grid (modal). */
  layout?: 'fixed' | 'inline';
}

export function BulkEditActionBar({ state, layout = 'fixed' }: BulkEditActionBarProps) {
  const { t } = useTranslation(['products']);
  const { selectedKeys, bulkAdjustPrice, bulkSetStatus, toggleAllSelected } = state;
  const count = selectedKeys.size;
  const isInline = layout === 'inline';

  if (count === 0) return null;

  return (
    <div
      className={cn(
        isInline
          ? 'crm-bulk-edit-modal__selection'
          : 'pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4',
      )}
      role="region"
      aria-label={t('products:bulkEditor.selectionBar')}
    >
      <div
        className={cn(
          isInline
            ? 'crm-bulk-edit-modal__selection-inner'
            : 'pointer-events-auto flex min-w-[min(100%,42rem)] items-center gap-4 rounded-2xl bg-foreground px-5 py-3 text-background shadow-2xl',
        )}
      >
        <div
          className={cn(
            'crm-bulk-edit-modal__selection-count flex items-center gap-2 text-sm font-medium',
            !isInline && 'border-r border-white/15 pr-4',
          )}
        >
          <span
            className={cn(
              'flex items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground',
              isInline ? 'crm-bulk-edit-modal__selection-badge' : 'size-7',
            )}
          >
            {count}
          </span>
          <span className={isInline ? 'text-foreground' : undefined}>
            {t('products:bulkEditor.selectedRows', { count })}
          </span>
        </div>

        {isInline ? <div className="crm-bulk-edit-modal__selection-divider" aria-hidden /> : null}

        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant={isInline ? 'outline' : 'secondary'}
                size="sm"
                className="h-8 gap-1.5"
              >
                <Percent className="size-3.5" />
                {t('products:bulkEditor.adjustPrice')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem onClick={() => bulkAdjustPrice(10)}>
                {t('products:bulkEditor.increasePrice', { percent: 10 })}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkAdjustPrice(5)}>
                {t('products:bulkEditor.increasePrice', { percent: 5 })}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => bulkAdjustPrice(-5)}>
                {t('products:bulkEditor.decreasePrice', { percent: 5 })}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkAdjustPrice(-10)}>
                {t('products:bulkEditor.decreasePrice', { percent: 10 })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant={isInline ? 'default' : undefined} size="sm" className="h-8 gap-1.5">
                <Zap className="size-3.5" />
                {t('products:bulkEditor.bulkActions')}
                <ChevronDown className="size-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem onClick={() => bulkSetStatus(true)}>
                <ToggleLeft className="mr-2 size-4" />
                {t('products:bulkEditor.setActive')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkSetStatus(false)}>
                <ToggleLeft className="mr-2 size-4" />
                {t('products:bulkEditor.setInactive')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            'size-8 shrink-0',
            isInline ? 'crm-bulk-edit-modal__selection-dismiss ml-auto' : 'ml-auto text-background/70 hover:bg-white/10 hover:text-background',
          )}
          onClick={() => toggleAllSelected(false)}
          aria-label={t('products:bulkEditor.clearSelection')}
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
}

interface BulkEditToolbarProps {
  state: BulkEditState;
}

export function BulkEditToolbar({ state }: BulkEditToolbarProps) {
  const { t } = useTranslation(['products']);

  return (
    <div className="crm-bulk-edit-card__toolbar">
      <div className="crm-bulk-edit-card__search">
        <Search className="crm-bulk-edit-card__search-icon" aria-hidden />
        <input
          type="text"
          role="searchbox"
          inputMode="search"
          autoComplete="off"
          spellCheck={false}
          value={state.searchInput}
          onChange={(e) => state.handleSearchChange(e.target.value)}
          placeholder={t('products:bulkEditor.searchPlaceholder')}
          className="crm-bulk-edit-card__search-input"
        />
      </div>

      <BulkEditColumnPicker
        visibleColumns={state.visibleColumns}
        toggleColumn={state.toggleColumn}
        isCreditEnabled={state.isCreditEnabled}
      />
    </div>
  );
}
